// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { useImages } from 'hooks';
import { flex, p12, Width } from 'styles';
import { ImageLoader } from 'components/Gui';
import type { AllImages } from 'hooks/use-images';

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const Image = styled.div`
  width: ${({ mobile }) => mobile};
  max-width: 420px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    order: 2;
    flex: 0 0 auto;
    width: ${({ tablet }) => tablet.imageContainer};
    max-width: unset;

    > div {
      width: 100%;
      min-width: ${({ tablet }) => `${tablet.image}px`};
    }
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    min-width: 598px;
    ${flex()};

    > div {
      width: ${({ desktop }) => `${desktop}px`};
      min-width: unset;
    }
  }
`;

const Text = styled.div`
  width: 100%;
  margin-top: 43px;
  text-align: center;
  max-width: 350px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 0;
    width: ${({ tablet }) => tablet.textContainer};
    text-align: left;
    max-width: unset;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: ${({ margin }) => margin}px;
    width: 468px;
  }

  h1 {
    margin-bottom: 20px;
  }

  p {
    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: 85%;
    }
  }
`;

const BREAK_ROW = 1100;

const Buttons = styled.div`
  margin-top: 29px;
  display: inline-flex;
  width: 100%;
  flex-direction: column;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 24px;
    width: unset;
  }

  @media print, screen and (min-width: ${BREAK_ROW}px) {
    display: flex;
    flex-direction: row;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 26px;
  }

  > div {
    margin: 0 auto 16px auto;
    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      margin: 0 0 16px 0;
    }
    @media print, screen and (min-width: ${BREAK_ROW}px) {
      margin-bottom: 0;
      margin-right: 16px;
    }
    &:last-child {
      margin-bottom: 0;
      @media print, screen and (min-width: ${BREAK_ROW}px) {
        margin-right: 0;
      }
    }
  }
`;

const Disclaimer = styled.div`
  ${p12};
  margin-top: 12px;
  text-align: center;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 14px;
    text-align: left;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 11px;
  }
`;

type Props = {
  image: {
    name: string,
    alt: string,
  },
  width?: {
    mobile?: string,
    tablet?: {
      imageContainer: string,
      textContainer: string,
      image: number,
    },
    desktop?: number,
  },
  textTopMargin?: number,
  text: Node,
  buttons: Node[],
  disclaimer: string | Node,
  images: AllImages,
};

const RightImageHero = ({
  width: { mobile = '100%', tablet = { imageContainer: '46%', textContainer: '46%', image: 430 }, desktop = 611 } = {},
  textTopMargin = 14,
  image: { name, alt },
  text,
  buttons,
  disclaimer,
  images,
  ...rest
}: Props) => {
  const heroImage = useImages(images)(name);
  return (
    <Wrapper {...rest}>
      <Image tablet={tablet} mobile={mobile} desktop={desktop}>
        <div>
          <ImageLoader image={heroImage} alt={alt} overrideLoading />
        </div>
      </Image>
      <Text tablet={tablet} margin={textTopMargin}>
        {text}
        <Buttons>{buttons}</Buttons>
        <Disclaimer>{disclaimer}</Disclaimer>
      </Text>
    </Wrapper>
  );
};

export default RightImageHero;
